import { useQuery } from '@tanstack/react-query';
import apiClient from '@/api/client';
import { AppLayout } from '@/components/Layout/AppLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Achievement {
  id: string;
  icon: string;
  title: string;
  description: string;
  unlocked: boolean;
  unlockedDetail?: string;
  rarity: 'häufig' | 'selten' | 'episch' | 'legendär';
}

// ─── API ─────────────────────────────────────────────────────────────────────

async function fetchAchievements(): Promise<Achievement[]> {
  const { data } = await apiClient.get<Achievement[]>('/achievements');
  return data;
}

// ─── Rarity config ───────────────────────────────────────────────────────────

const RARITY_CONFIG = {
  legendär: {
    badge: 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold',
    border: 'border-l-4 border-amber-400',
    bg: 'bg-gradient-to-br from-amber-50 to-white',
    label: 'Legendär',
  },
  episch: {
    badge: 'bg-purple-100 text-purple-700 text-[10px] px-1.5 py-0.5 rounded-full font-bold',
    border: 'border-l-4 border-purple-400',
    bg: 'bg-purple-50/30',
    label: 'Episch',
  },
  selten: {
    badge: 'bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0.5 rounded-full font-bold',
    border: 'border-l-4 border-blue-400',
    bg: 'bg-blue-50/30',
    label: 'Selten',
  },
  häufig: {
    badge: 'bg-slate-100 text-slate-500 text-[10px] px-1.5 py-0.5 rounded-full font-bold',
    border: 'border-l-4 border-slate-200',
    bg: 'bg-white',
    label: 'Häufig',
  },
} as const;

// ─── Achievement Card ─────────────────────────────────────────────────────────

function AchievementCard({ achievement }: { achievement: Achievement }) {
  const config = RARITY_CONFIG[achievement.rarity];

  if (!achievement.unlocked) {
    return (
      <div
        className="relative rounded-xl border border-slate-200 bg-slate-100 p-4 flex flex-col items-center gap-2 overflow-hidden"
        style={{ filter: 'grayscale(1)', opacity: 0.45 }}
      >
        <span className="absolute top-2 right-2 text-[10px] font-bold bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded-full uppercase tracking-wide">
          {config.label}
        </span>
        <span className="text-4xl mt-2">{achievement.icon}</span>
        <p className="font-semibold text-sm text-slate-700 text-center leading-tight">
          {achievement.title}
        </p>
        <p className="text-xs text-slate-500 text-center leading-snug">
          {achievement.description}
        </p>
        <span className="absolute bottom-2 right-2 text-xl">🔒</span>
        <p className="text-[10px] font-bold tracking-widest text-slate-400 uppercase mt-auto">
          GESPERRT
        </p>
      </div>
    );
  }

  return (
    <div
      className={`relative rounded-xl border border-slate-200 shadow-sm p-4 flex flex-col items-center gap-2 overflow-hidden ${config.border} ${config.bg}`}
    >
      <span className={`absolute top-2 right-2 ${config.badge}`}>
        {config.label}
      </span>
      <span className="text-4xl mt-2">{achievement.icon}</span>
      <p className="font-semibold text-sm text-slate-800 text-center leading-tight">
        {achievement.title}
      </p>
      <p className="text-xs text-slate-500 text-center leading-snug">
        {achievement.description}
      </p>
      {achievement.unlockedDetail && (
        <p className="text-[11px] text-green-600 italic text-center mt-auto leading-snug">
          ✓ {achievement.unlockedDetail}
        </p>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AchievementsPage() {
  const { data: achievements, isLoading, isError } = useQuery({
    queryKey: ['achievements'],
    queryFn: fetchAchievements,
  });

  const total = achievements?.length ?? 0;
  const unlocked = achievements?.filter((a) => a.unlocked).length ?? 0;
  const pct = total > 0 ? Math.round((unlocked / total) * 100) : 0;

  return (
    <AppLayout>
      {/* ── Header ── */}
      <div className="bg-slate-900 text-white px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl font-bold tracking-tight mb-1">
            🏆 Trophäenraum
          </h1>
          <p className="text-slate-400 text-sm mb-6">
            Deine freigeschalteten Errungenschaften auf einen Blick.
          </p>

          {/* Stats row */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <span className="text-slate-300 text-sm font-medium whitespace-nowrap">
              {unlocked} von {total} freigeschaltet
            </span>
            <div className="flex-1 bg-slate-700 rounded-full h-3 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ${
                  pct > 0
                    ? 'bg-gradient-to-r from-yellow-400 via-amber-400 to-amber-500'
                    : 'bg-slate-600'
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-amber-400 font-bold text-sm tabular-nums">
              {pct}%
            </span>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {isLoading && (
          <div className="flex justify-center py-20">
            <LoadingSpinner />
          </div>
        )}

        {isError && (
          <div className="text-center py-20 text-red-500">
            Errungenschaften konnten nicht geladen werden.
          </div>
        )}

        {achievements && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {achievements.map((achievement) => (
              <AchievementCard key={achievement.id} achievement={achievement} />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
